import { Component, Input, OnInit } from '@angular/core';
import { NgFor } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FolderPlanogram } from 'src/app/models/folder';

/** A selected planogram condensed for display inside a data-source card. */
interface GroupedPlanogram {
  name: string;
  storeName: string;
  merchName: string;
}

/** Selected planograms that all share one data source. */
interface DataSourceGroup {
  dataSourceId: string;
  dataSourceName: string;
  planograms: GroupedPlanogram[];
}

/**
 * Shown before navigating to the bulk-clone page when the user's selection spans
 * more than one data source. Bulk clone targets a single store (and therefore a
 * single data source), so a mixed selection can't be cloned together. This lists
 * the selection grouped by data source so the user can see the split, and offers
 * a shortcut to keep only one group and proceed.
 *
 * Resolves to the chosen data source id (string) when the user picks "clone only
 * these", or is dismissed (null) when they choose to adjust the selection manually.
 */
@Component({
  selector: 'app-cross-datasource-warning-modal',
  templateUrl: './cross-datasource-warning-modal.component.html',
  imports: [NgFor],
})
export class CrossDatasourceWarningModalComponent implements OnInit {
  @Input() planograms: FolderPlanogram[] = [];

  groups: DataSourceGroup[] = [];

  constructor(public activeModal: NgbActiveModal) {}

  ngOnInit(): void {
    const byDataSource = new Map<string, DataSourceGroup>();
    for (const planogram of this.planograms) {
      const id = planogram.dataSourceId ?? '';
      let group = byDataSource.get(id);
      if (!group) {
        group = {
          dataSourceId: id,
          dataSourceName: this.dataSourceLabel(planogram),
          planograms: [],
        };
        byDataSource.set(id, group);
      }
      group.planograms.push({
        name: this.planogramName(planogram),
        storeName: planogram.storeName,
        merchName: planogram.merchName,
      });
    }
    this.groups = Array.from(byDataSource.values()).sort((a, b) =>
      a.dataSourceName.localeCompare(b.dataSourceName)
    );
  }

  /** Keep only this data source's planograms and continue to the bulk-clone page. */
  chooseDataSource(group: DataSourceGroup): void {
    this.activeModal.close(group.dataSourceId);
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  private dataSourceLabel(planogram: FolderPlanogram): string {
    const name = (planogram.dataSourceName ?? '').trim();
    if (name) return name;
    return 'Unknown data source';
  }

  private planogramName(planogram: FolderPlanogram): string {
    if (planogram.referencePlanogramId) {
      return planogram.referenceName || 'Linked Planogram';
    }
    if (planogram.states && planogram.states.length > 0) {
      const latest = [...planogram.states].sort(
        (a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
      )[0];
      return latest.name || 'Unnamed Planogram';
    }
    return 'Unnamed Planogram';
  }
}
